import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Link2, LogIn, Plus, Minus } from 'lucide-react';
import { trackEvent } from '@/lib/faro';

interface ShareLinkData {
    id: number;
    label: string | null;
    mode: 'add' | 'remove' | 'both';
    min_amount: string | null;
    max_amount: string | null;
    require_login: boolean;
    contract: {
        id: number;
        title: string;
        current_balance: string;
        dom: { display_name: string; avatar_path?: string } | null;
        sub: { display_name: string; avatar_path?: string } | null;
    };
}

export default function ShareLinkPage() {
    const { token } = useParams<{ token: string }>();
    const { user } = useAuth();
    const [action, setAction] = useState<'add' | 'remove'>('add');
    const [amount, setAmount] = useState('');
    const [message, setMessage] = useState('');
    const [guestName, setGuestName] = useState('');
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');

    const { data, isLoading, error: loadError } = useQuery({
        queryKey: ['share-link', token],
        queryFn: () => api.get<{ data: ShareLinkData }>(`/share-links/${token}`).then(r => r.data),
        enabled: !!token,
    });

    const useMutation_ = useMutation({
        mutationFn: () => api.post<{ message: string; new_balance: string }>(`/share-links/${token}/use`, {
            action,
            amount: parseFloat(amount),
            message: message || undefined,
            guest_name: guestName || undefined,
        }),
        onSuccess: (res) => {
            trackEvent('share_link_used', { action, amount: parseFloat(amount) });
            setSuccess(res.message);
            setAmount('');
            setMessage('');
            setError('');
        },
        onError: (err: Error) => {
            setError(err instanceof ApiError ? err.message : 'Something went wrong');
            setSuccess('');
        },
    });

    if (isLoading) {
        return <p className="text-center text-sm text-slate-500">Loading...</p>;
    }

    if (loadError || !data) {
        return (
            <div className="text-center space-y-4">
                <p className="text-sm text-red-400">This share link is not available.</p>
                <Link to="/" className="text-sm text-violet-400 hover:underline">
                    Go to DebtBound
                </Link>
            </div>
        );
    }

    const needsLogin = data.require_login && !user;
    const showAdd = data.mode === 'add' || data.mode === 'both';
    const showRemove = data.mode === 'remove' || data.mode === 'both';

    // Set default action based on mode
    if (data.mode === 'remove' && action === 'add') {
        setAction('remove');
    }

    return (
        <div className="mx-auto max-w-md space-y-6">
            <div className="text-center">
                <h1 className="flex items-center justify-center gap-2 text-2xl font-bold text-violet-400">
                    <Link2 className="h-6 w-6" />
                    {data.label || 'Share Link'}
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                    {data.contract.title}
                </p>
            </div>

            <Card>
                <CardContent className="py-4 space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs text-slate-500">Contract between</p>
                            <p className="text-sm">
                                <span className="text-violet-400">{data.contract.dom?.display_name ?? 'Dom'}</span>
                                {' & '}
                                <span className="text-orange-400">{data.contract.sub?.display_name ?? 'Sub'}</span>
                            </p>
                        </div>
                        <div className="text-right">
                            <p className="text-xs text-slate-500">Current Balance</p>
                            <p className="text-lg font-bold text-red-400">${parseFloat(data.contract.current_balance).toFixed(2)}</p>
                        </div>
                    </div>

                    {needsLogin ? (
                        <div className="text-center space-y-3 py-4">
                            <p className="text-sm text-slate-400">You must be logged in to use this link.</p>
                            <div className="flex justify-center gap-3">
                                <Button size="sm" asChild className="bg-violet-600 hover:bg-violet-700">
                                    <Link to="/login">
                                        <LogIn className="h-3.5 w-3.5 mr-1 inline" />
                                        Sign In
                                    </Link>
                                </Button>
                                <Button size="sm" variant="ghost" asChild className="text-slate-400">
                                    <Link to="/register">Create Account</Link>
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {data.mode === 'both' && (
                                <div className="flex gap-2">
                                    <Button
                                        size="sm"
                                        variant={action === 'add' ? 'default' : 'outline'}
                                        className="flex-1"
                                        onClick={() => setAction('add')}
                                    >
                                        <Plus className="h-3 w-3 mr-1" /> Add to Debt
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant={action === 'remove' ? 'default' : 'outline'}
                                        className="flex-1"
                                        onClick={() => setAction('remove')}
                                    >
                                        <Minus className="h-3 w-3 mr-1" /> Pay Down
                                    </Button>
                                </div>
                            )}

                            <div>
                                <label className="text-xs text-slate-400">Amount ($)</label>
                                <Input
                                    type="number"
                                    min="0.01"
                                    step="0.01"
                                    value={amount}
                                    onChange={e => setAmount(e.target.value)}
                                    placeholder={
                                        data.min_amount && data.max_amount
                                            ? `$${parseFloat(data.min_amount).toFixed(2)} - $${parseFloat(data.max_amount).toFixed(2)}`
                                            : '0.00'
                                    }
                                />
                            </div>

                            {!user && (
                                <div>
                                    <label className="text-xs text-slate-400">Your Name (optional)</label>
                                    <Input
                                        value={guestName}
                                        onChange={e => setGuestName(e.target.value)}
                                        placeholder="Anonymous"
                                    />
                                </div>
                            )}

                            <div>
                                <label className="text-xs text-slate-400">Message (optional)</label>
                                <Input
                                    value={message}
                                    onChange={e => setMessage(e.target.value)}
                                    placeholder="Leave a note..."
                                />
                            </div>

                            {error && <p className="text-xs text-red-400">{error}</p>}
                            {success && <p className="text-xs text-green-400">{success}</p>}

                            <Button
                                className="w-full"
                                disabled={!amount || parseFloat(amount) <= 0 || useMutation_.isPending}
                                onClick={() => useMutation_.mutate()}
                            >
                                {useMutation_.isPending
                                    ? 'Processing...'
                                    : action === 'add'
                                        ? `Add $${amount || '0.00'} to Balance`
                                        : `Pay $${amount || '0.00'} Toward Balance`}
                            </Button>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
